import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { CompanyDocumentsFormComponent } from '../../components/company-documents-form/company-documents-form.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { CompanyDocumentRevisionsComponent } from '../../components/company-document-revisions/company-document-revisions.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { CommonService } from '../../../../services/common.service';

import {
  CompanyDocument,
  CompanyDocumentRevision,
  companyDocumentRevisionInProgressId,
  companyDocumentUserFullName
} from '../../../../models/entities/company-document';

@Component({
  selector: 'app-company-documents-detail-page',
  imports: [LoaderComponent, CompanyDocumentsFormComponent, CompanyDocumentRevisionsComponent],
  templateUrl: './company-documents-detail-page.component.html',
  styleUrl: './company-documents-detail-page.component.scss',
})
export class CompanyDocumentsDetailPageComponent implements OnInit, OnDestroy {

  @ViewChild(CompanyDocumentRevisionsComponent) revisionsPanel?: CompanyDocumentRevisionsComponent;

  companyDocumentId?: number;
  companyDocument?: CompanyDocument;

  canModify: boolean = false;
  canDeleteRevision: boolean = false;
  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Loading...',
    overlay: true
  };

  private currentUserId: number | null = null;
  private hasDeleteAccess: boolean = false;
  private readonly LOADER_CONTEXT = 'company-documents-detail-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserId = user?.userId != null ? Number(user.userId) : null;
        this.refreshRevisionDeletePermission();
      });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
          'modify'
        );
        this.hasDeleteAccess = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
          'delete'
        );
        this.refreshRevisionDeletePermission();
      });

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    const paramId = this.route.snapshot.paramMap.get('companyDocumentId');
    if (paramId) {
      this.companyDocumentId = Number(paramId);
      this.loadCompanyDocument(this.companyDocumentId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get code(): string {
    return this.companyDocument?.code ?? '';
  }

  get department(): string {
    return this.companyDocument?.department ?? '-';
  }

  get name(): string {
    return this.companyDocument?.name ?? '-';
  }

  get groupName(): string {
    return this.companyDocument?.companyDocumentGroup?.name || '-';
  }

  get creatorLabel(): string {
    return companyDocumentUserFullName(this.companyDocument?.creatorUser) || '-';
  }

  get createTimeLabel(): string {
    const value = this.companyDocument?.createTime;
    if (!value) {
      return '';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : this.commonService.formatDate(date);
  }

  get currentRevisionCode(): string {
    return this.companyDocument?.currentRevisionCode || '-';
  }

  get revisionInProgressId(): number | null {
    return companyDocumentRevisionInProgressId(this.companyDocument);
  }

  get revisionInProgressCode(): string {
    return this.companyDocument?.revisionInProgress?.code || '';
  }

  goToEdit(): void {
    if (!this.companyDocumentId) {
      return;
    }
    this.routeService.companyDocumentsUpdate(this.companyDocumentId);
  }

  goToNewRevision(): void {
    if (!this.companyDocumentId) {
      return;
    }

    if (this.revisionInProgressId) {
      this.routeService.companyDocumentsRevisionDetail(this.revisionInProgressId);
      return;
    }

    this.routeService.companyDocumentsRevisionCreate(this.companyDocumentId);
  }

  onOpenRevision(revision: CompanyDocumentRevision): void {
    if (!revision?.companyDocumentRevisionId) {
      return;
    }
    this.routeService.companyDocumentsRevisionDetail(Number(revision.companyDocumentRevisionId));
  }

  private refreshRevisionDeletePermission(): void {
    const isCreator = this.currentUserId != null
      && Number(this.companyDocument?.creatorUser?.userId) === this.currentUserId;

    this.canDeleteRevision = this.hasDeleteAccess || isCreator;
  }

  private loadCompanyDocument(companyDocumentId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailCompanyDocument(companyDocumentId).subscribe({
      next: (response: any) => {
        const companyDocument: CompanyDocument | null =
          this.qualityManagementService.readResponseKey(response, 'companyDocument') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!companyDocument) {
          this.alertNotificationService.error('Company document not found.');
          return;
        }

        this.companyDocument = companyDocument;
        this.refreshRevisionDeletePermission();
      },
      error: (error: any) => {
        console.error('Error loading the company document:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to load the company document.')
        );
      }
    });
  }
}
